import React, { useRef, useEffect } from "react";
import { typesetMath, convertMathJax } from "modules/assessment/AssessmentApp";

/**
 * Renders different types of questions based on their type.
 * @param {object} props - The component props.
 * @param {object} props.question - The question data object.
 * @param {number} props.index - The index of the question.
 * @param {boolean} props.isPaperView - Whether to render in examination paper mode.
 */
export const QuestionRenderer = ({ question, index, isPaperView = false }) => {
    const questionRef = useRef(null);

    useEffect(() => {
        if (typeof typesetMath === 'function') {
            typesetMath(questionRef.current);
        }
    }, [question]);

    if (!question) return null;

    const { questionType, questionText, details = [] } = question;

    const renderDetail = (detail, detailIndex) => {
        switch (questionType) {
            case "MULTIPLE_CHOICE":
            case "OPEN_ENDED":
                return (
                    <ul
                        key={detailIndex}
                        className={`list-disc pl-5 mt-2 ${isPaperView ? 'text-black' : 'text-black dark:text-white'}`}
                    >
                        <li>{convertMathJax(detail?.questionChoice?.trim())}</li>
                    </ul>
                );
            case "MATCHING":
                const rows = (detail?.questionLeft || "")
                    .split(",")
                    .map((cell) => cell.trim());
                const cols = (detail?.questionRight || "")
                    .split(",")
                    .map((cell) => cell.trim());
                return (
                    <table
                        key={detailIndex}
                        className={`table-auto w-full my-4 ${isPaperView ? 'border border-black' : 'border border-gray-300 dark:border-gray-700 rounded-lg'}`}
                    >
                        <tbody>
                            {rows.map((rowText, idx) => {
                                const colText = cols[idx] || "";
                                return (
                                    <tr key={idx}>
                                        <td className={`w-1/2 p-2 font-medium ${isPaperView ? 'text-black border-r border-black' : 'text-black dark:text-white border-r border-gray-300 dark:border-gray-600'}`}>
                                            {convertMathJax(rowText)}
                                        </td>
                                        <td className={`w-1/2 p-2 text-right ${isPaperView ? 'text-black' : 'text-gray-700 dark:text-gray-300'}`}>
                                            {convertMathJax(colText)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                );
            case "QUESTION_TABLE":
                const tableRows = (detail?.questionCsv || "")
                    .split("\n")
                    .map((line) => line.split(",").map((cell) => cell.trim()));
                return (
                    <table
                        key={detailIndex}
                        className={`table-auto w-full my-4 ${isPaperView ? 'border border-black' : 'border border-gray-300 dark:border-gray-700 rounded-lg'}`}
                    >
                        <tbody>
                            {tableRows.map((row, rowIdx) => (
                                <tr
                                    key={rowIdx}
                                    className={isPaperView ? 'border-t border-black last:border-b-0' : 'border-t border-gray-200 dark:border-gray-800 last:border-b-0'}
                                >
                                    {row.map((cell, colIdx) => (
                                        <td
                                            key={colIdx}
                                            className={`p-3 text-center ${isPaperView ? 'text-black border-r border-black last:border-r-0' : 'text-black dark:text-white'}`}
                                        >
                                            {convertMathJax(cell)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                );
            case "MATHS_CALCULATION":
                return (
                    <pre
                        key={detailIndex}
                        className={`${isPaperView ? 'bg-transparent text-black' : 'bg-gray-100 dark:bg-gray-800 text-black dark:text-white rounded-md'} p-4 overflow-x-auto font-mono my-4`}
                    >
                        {convertMathJax(detail?.questionChoice?.trim())} {detail?.questionMarkup}
                    </pre>
                );
            default:
                return null;
        }
    };

    return (
        <div ref={questionRef} className={isPaperView ? "mb-2" : "p-6 rounded-xl mb-6"}>
            <div className={isPaperView ? "text-base text-black flex items-start" : "text-xl font-semibold mb-4 text-black dark:text-white"}>
                {isPaperView ? (
                    <>
                        <span className="mr-2 font-bold">{index + 1}.</span>
                        <div className="flex-1">
                            {convertMathJax(questionText)}
                        </div>
                    </>
                ) : (
                    convertMathJax(`${index + 1}. ${questionText}`)
                )}
            </div>
            
            <div className={`question-content ${isPaperView ? 'ml-6' : ''}`}>
                {details && [...details].sort((a, b) => a.id - b.id).map((detail, idx) => renderDetail(detail, idx))}

                {question.pictureUrl && (
                    <div className={isPaperView ? "my-4" : "flex justify-center my-4"}>
                        <img
                            src={`${process.env.PUBLIC_URL}/images/question/${question.pictureUrl}.png`}
                            alt="question illustration"
                            className={isPaperView ? "max-w-md border border-gray-300" : "rounded-lg"} />
                    </div>
                )}
            </div>
        </div>
    );
};
